import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Camera, ClipboardCheck, Loader2, Pencil, Wand2, AlertCircle, RefreshCw, Send, Sun, Ruler, Home, Ban } from "lucide-react";
import { Button } from "./ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { ImproveQuestionsV3, V3_DEFAULTS } from "./IntakeQuestions";
import { generateConcept, getSession, updateIntake } from "../lib/api";
import ConceptResults from "./ConceptResults";

const TIPS = [
  { icon: Ruler, text: "Stand far enough back to capture the complete project area" },
  { icon: Sun, text: "Photograph in daylight, avoiding heavy shadows or glare" },
  { icon: Camera, text: "Keep the camera level" },
  { icon: Home, text: "Include the home, landscape bed, lawn or other fixed reference points" },
  { icon: Ban, text: "Avoid people, vehicles and temporary objects when possible" },
];

const join = (v) => (Array.isArray(v) ? v.join(", ") : v) || "—";

function Card({ testId, children, tone = "green" }) {
  const b = tone === "amber" ? "border-amber-200 bg-amber-50/60" : tone === "rose" ? "border-rose-200 bg-white" : "border-emerald-200 bg-white";
  return <section data-testid={testId} className={`rounded-2xl border p-5 ${b}`}>{children}</section>;
}

function PhotoQualityNotice({ issues }) {
  const navigate = useNavigate();
  return (
    <Card testId="photo-quality-notice" tone="amber">
      <div className="flex items-center gap-2 font-display text-lg font-bold text-[#092C15]">
        <Camera size={18} className="text-amber-600" /> We need a better photo for your concept
      </div>
      <p className="mt-1 text-sm text-slate-700">Your recommendations are ready, but this primary photo can't support a realistic visualization yet.</p>
      {issues?.length > 0 && (
        <ul className="mt-3 space-y-1 text-sm text-amber-900" data-testid="photo-quality-issues">
          {issues.map((i, k) => <li key={k}>• {i}</li>)}
        </ul>
      )}
      <div className="mt-4 text-[11px] uppercase tracking-widest font-bold text-emerald-700">Photo tips</div>
      <ul className="mt-2 space-y-1.5 text-sm text-slate-700">
        {TIPS.map(({ icon: Icon, text }) => (
          <li key={text} className="flex gap-2"><Icon size={15} className="mt-0.5 text-emerald-600 shrink-0" /> {text}</li>
        ))}
      </ul>
      <Button data-testid="replace-photo-button" onClick={() => navigate("/upload")} className="mt-4 w-full h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 font-semibold">
        <Camera size={16} className="mr-2" /> Replace My Primary Photo
      </Button>
    </Card>
  );
}

function Row({ label, value, testId }) {
  return (
    <div className="py-2 border-b border-slate-100 last:border-0" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-widest font-bold text-emerald-700">{label}</div>
      <div className="text-sm text-slate-700 mt-0.5">{value}</div>
    </div>
  );
}

function EditPreferences({ open, onOpenChange, session, setSession }) {
  const [value, setValue] = useState({ ...V3_DEFAULTS, ...(session.intake || {}) });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!value.improvement_goals?.length) return toast.error("Please choose at least one improvement.");
    setSaving(true);
    try {
      setSession(await updateIntake(session.id, value));
      onOpenChange(false);
      toast.success("Preferences updated. Refreshing your recommendations…");
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Could not save preferences.");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-lg" data-testid="edit-preferences-dialog">
        <DialogHeader><DialogTitle>Edit My Preferences</DialogTitle></DialogHeader>
        <ImproveQuestionsV3 value={value} onChange={setValue} />
        <Button data-testid="save-preferences-button" onClick={save} disabled={saving} className="w-full h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 font-semibold">
          {saving && <Loader2 size={16} className="mr-2 animate-spin" />} Save & Update Recommendations
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function DesignDirectionReview({ session, setSession }) {
  const i = session.intake || {};
  const a = session.assessment || {};
  const [editOpen, setEditOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const generate = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await generateConcept(session.id);
      setSession({ ...session, concept: { ...(session.concept || {}), status: "processing" } });
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Could not start your concept.");
    } finally {
      setBusy(false);
      setConfirmOpen(false);
    }
  };
  const preserve = [i.preserve_areas, i.preserve_features].filter(Boolean).join(" · ") || "Nothing specified";
  return (
    <Card testId="design-direction-review">
      <div className="flex items-center gap-2 font-display text-lg font-bold text-[#092C15]">
        <ClipboardCheck size={18} className="text-emerald-600" /> Review Your GreenFlow Design Direction
      </div>
      <p className="mt-1 text-sm text-slate-600">Check these details before we create your landscape concept.</p>
      <div className="mt-3">
        <Row testId="review-style" label="General style" value={join(i.landscape_style)} />
        <Row testId="review-improvements" label="Requested improvements" value={join(i.improvement_goals)} />
        <Row testId="review-preserve" label="Elements to preserve" value={preserve} />
        <Row testId="review-categories" label="Proposed enhancement categories" value={join((a.opportunities || []).map((o) => o.category))} />
        <Row testId="review-maintenance" label="Maintenance preference" value={i.maintenance_level || "—"} />
        <Row testId="review-budget" label="Budget guidance" value={<>{i.budget_range || "—"} <span className="block text-xs text-slate-500">For concept guidance only — not a GreenFlow estimate or quotation.</span></>} />
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <Button data-testid="edit-preferences-button" variant="outline" onClick={() => setEditOpen(true)} className="h-12 rounded-xl border-emerald-300 text-emerald-800 font-semibold">
          <Pencil size={16} className="mr-2" /> EDIT MY PREFERENCES
        </Button>
        <Button data-testid="generate-concept-button" onClick={() => setConfirmOpen(true)} disabled={busy} className="h-12 rounded-xl bg-[#092C15] hover:bg-emerald-900 text-white font-semibold whitespace-normal">
          <Wand2 size={16} className="mr-2" /> GENERATE MY LANDSCAPE CONCEPT
        </Button>
      </div>
      <EditPreferences open={editOpen} onOpenChange={setEditOpen} session={session} setSession={setSession} />
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="generate-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Create your landscape concept?</AlertDialogTitle>
            <AlertDialogDescription>
              Your concept is a conceptual visualization based on your photo. It is not a final landscape plan, proposal,
              estimate or installation guarantee. One concept is created per submission.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="generate-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="generate-confirm-button" disabled={busy} onClick={(e) => { e.preventDefault(); generate(); }} className="bg-[#092C15] hover:bg-emerald-900">
              {busy && <Loader2 size={14} className="mr-2 animate-spin" />} I Understand — Generate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

function Generating() {
  return (
    <Card testId="concept-generating">
      <div className="text-center py-4">
        <Loader2 className="mx-auto animate-spin text-emerald-600" size={32} />
        <div className="mt-3 font-display font-bold text-[#092C15]">Creating your GreenFlow Landscape Vision…</div>
        <div className="mt-1 text-sm text-slate-500">This can take up to a minute. Please keep this screen open.</div>
      </div>
    </Card>
  );
}

function ConceptError({ session, setSession }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [canRetry, setCanRetry] = useState((session.concept?.attempts || 0) < 2);
  const retry = async () => {
    setBusy(true);
    try {
      await generateConcept(session.id);
      setSession({ ...session, concept: { ...(session.concept || {}), status: "processing" } });
    } catch (ex) {
      setCanRetry(false);
      toast.error(ex?.response?.data?.detail || "Retry isn't available.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card testId="concept-error" tone="rose">
      <div className="flex items-center gap-2 font-display text-lg font-bold text-[#092C15]">
        <AlertCircle size={18} className="text-rose-600" /> We couldn't complete your concept
      </div>
      <p className="mt-1 text-sm text-slate-600">
        Your submission, photos and recommendations are safely saved. You can try again, or send your project to
        GreenFlow for a personal review.
      </p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {canRetry && (
          <Button data-testid="concept-retry-button" variant="outline" onClick={retry} disabled={busy} className="h-12 rounded-xl font-semibold">
            {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <RefreshCw size={16} className="mr-2" />} Try Again
          </Button>
        )}
        <Button data-testid="manual-review-button" onClick={() => navigate("/vision?manual=1")} className="h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 font-semibold whitespace-normal">
          <Send size={16} className="mr-2" /> Submit for GreenFlow Review
        </Button>
      </div>
    </Card>
  );
}

export default function ConceptFlow({ session, setSession, cfg, children }) {
  const status = session.concept?.status;
  useEffect(() => {
    if (status !== "processing") return undefined;
    const tick = async () => {
      try {
        const fresh = await getSession(session.id);
        if (fresh.concept?.status !== "processing") setSession(fresh);
      } catch {
        /* keep polling */
      }
    };
    const t = setInterval(tick, 4000);
    const onVisible = () => { if (document.visibilityState === "visible") tick(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [session.id, status]); // eslint-disable-line react-hooks/exhaustive-deps

  const q = session.assessment?.photo_quality;
  if (status === "complete") return <ConceptResults session={session} setSession={setSession} cfg={cfg}>{children}</ConceptResults>;
  if (status === "processing") return <Generating />;
  if (status === "error") return <ConceptError session={session} setSession={setSession} />;
  if (!q?.usable) return <PhotoQualityNotice issues={q?.issues} />;
  return <DesignDirectionReview session={session} setSession={setSession} />;
}
